// src/components/LandingPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../contexts/UserContext';

export default function LandingPage() {
  const navigate = useNavigate();
  const { firebaseUser, loading: authLoading } = useCurrentUser();
  const [reducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [isDevicesVisible, setIsDevicesVisible] = useState(false);
  const [canPlayVideo, setCanPlayVideo] = useState(false);
  const devicesContainerRef = useRef<HTMLDivElement>(null);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && firebaseUser && !firebaseUser.isAnonymous) {
      navigate('/dashboard', { replace: true });
    }
  }, [firebaseUser, authLoading, navigate]);

  // Force dark mode on landing page
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light');
    root.classList.add('dark');

    return () => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode !== null) {
        const shouldUseDark = JSON.parse(savedDarkMode);
        root.classList.toggle('dark', shouldUseDark);
        root.classList.toggle('light', !shouldUseDark);
        return;
      }
      root.classList.remove('dark');
    };
  }, []);

  // Landing page overflow control — allow vertical scroll for small screens
  useEffect(() => {
    document.documentElement.classList.remove('editor2-no-scroll');
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    document.getElementById('root')?.classList.add('allow-scroll');
    return () => {
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      document.getElementById('root')?.classList.remove('allow-scroll');
    };
  }, []);

  // Lazy-load: only start videos when devices section is in viewport
  useEffect(() => {
    const el = devicesContainerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsDevicesVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsDevicesVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Bandwidth gate: skip video playback on slow connections or data saver
  useEffect(() => {
    if (reducedMotion) {
      setCanPlayVideo(false);
      return;
    }
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    const saveData = connection?.saveData ?? false;
    const slowConnection =
      connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g';
    setCanPlayVideo(!saveData && !slowConnection);
  }, [reducedMotion]);

  // Video refs - one per device
  const laptopVideoRef = useRef<HTMLVideoElement>(null);
  const ipadVideoRef = useRef<HTMLVideoElement>(null);
  const iphoneVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let isMounted = true;

    // If reduced motion or slow network, show device mockups without downloading video
    if (reducedMotion || !canPlayVideo) {
      [laptopVideoRef, ipadVideoRef, iphoneVideoRef].forEach(ref => {
        if (ref.current) ref.current.style.opacity = '1';
      });
      return;
    }

    // Wait until devices section is in viewport
    if (!isDevicesVisible) return;

    const playSequential = (
      ref: React.RefObject<HTMLVideoElement | null>,
      videos: string[],
      delay: number
    ) => {
      let i = 0;
      let isPlaying = false;
      let isFirst = true;

      // Preload next video metadata (skip on slow/data-saver connections)
      const preloadVideo = (src: string) => {
        const conn = (navigator as Navigator & {
          connection?: { saveData?: boolean; effectiveType?: string };
        }).connection;
        if (conn?.saveData || conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g') return;
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.src = src;
      };

      const playNext = async () => {
        if (!isMounted || !ref.current || isPlaying) return;
        isPlaying = true;
        const video = ref.current;

        try {
          // Crossfade out (skip on first play)
          if (!isFirst) {
            video.style.opacity = '0';
            await new Promise((r) => setTimeout(r, 300));
          }

          if (!isMounted || !ref.current) return;

          video.src = videos[i];
          await new Promise<void>((resolve, reject) => {
            if (!isMounted || !ref.current) return reject('unmounted');
            const onLoaded = () => {
              ref.current?.removeEventListener('loadeddata', onLoaded);
              ref.current?.removeEventListener('error', onErr);
              resolve();
            };
            const onErr = (e: Event) => {
              ref.current?.removeEventListener('loadeddata', onLoaded);
              ref.current?.removeEventListener('error', onErr);
              reject(e);
            };
            ref.current.addEventListener('loadeddata', onLoaded);
            ref.current.addEventListener('error', onErr);
            ref.current.load();
          });

          if (!isMounted || !ref.current) return;

          // Crossfade in
          video.style.opacity = '1';
          await ref.current.play();
          isFirst = false;

          // Preload the next video in the background
          const nextI = (i + 1) % videos.length;
          preloadVideo(videos[nextI]);

          const onEnded = () => {
            ref.current?.removeEventListener('ended', onEnded);
            isPlaying = false;
            i = (i + 1) % videos.length;
            if (isMounted) setTimeout(playNext, 500);
          };
          ref.current.addEventListener('ended', onEnded);
        } catch {
          isPlaying = false;
          i = (i + 1) % videos.length;
          if (isMounted) setTimeout(playNext, 1000);
        }
      };

      setTimeout(playNext, delay);
    };

    // 3 videos per device, staggered start
    playSequential(laptopVideoRef, ['/videos/video1.mp4', '/videos/video2.mp4', '/videos/video1-ads.mp4'], 800);
    playSequential(ipadVideoRef, ['/videos/video3.mp4', '/videos/video4.mp4', '/videos/video2-ads.mp4'], 1200);
    playSequential(iphoneVideoRef, ['/videos/video5.mp4', '/videos/video6.mp4', '/videos/video3-ads.mp4'], 1600);

    return () => {
      isMounted = false;
      // Full cleanup: stop playback and release video resources
      [laptopVideoRef, ipadVideoRef, iphoneVideoRef].forEach(ref => {
        const video = ref.current;
        if (!video) return;
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {
          // Ignore errors during cleanup
        }
      });
    };
  }, [reducedMotion, isDevicesVisible, canPlayVideo]);

  // Shared video inline styles with crossfade transition
  const videoTransition = { transition: 'opacity 0.3s ease-in-out' };

  return (
    <div
      data-testid="page-landing"
      className="relative w-full min-h-screen text-white overflow-x-hidden"
      style={{
        backgroundImage: "url('/images/satin-phone-bg.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Logo - top left, large and comfortable */}
      <div className="absolute top-6 left-8 z-50 flex items-center gap-3">
        <img
          src="/images/viewsboost-logo.png"
          alt="ViewsBoost"
          className="h-11 w-11 md:h-14 md:w-14"
        />
        <span className="text-xl md:text-3xl font-bold text-yellow-400 tracking-tight">
          ViewsBoost
        </span>
      </div>

      {/* Hero Text */}
      <div className="relative z-40 text-center pt-24 md:pt-28 px-6">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-none">
          Watch. <span className="text-yellow-400 italic">Create.</span> Earn.
        </h1>
        <p className="mt-5 text-base md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
          Watch videos, edit in our studio, create with AI, and earn along the way.
        </p>
      </div>

      {/* === Fire / Golden glow behind devices (Context.png style) === */}
      {/* Main warm fire glow - center behind devices */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-10 pointer-events-none"
        style={{
          top: '32%',
          width: '60%',
          height: '55%',
          background:
            'radial-gradient(ellipse at 50% 60%, rgba(255, 170, 30, 0.35) 0%, rgba(220, 130, 20, 0.2) 25%, rgba(180, 100, 10, 0.1) 45%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      {/* Left-side warm accent (behind laptop) */}
      <div
        className="absolute z-10 pointer-events-none hidden sm:block"
        style={{
          top: '42%',
          left: '10%',
          width: '30%',
          height: '40%',
          background:
            'radial-gradient(ellipse at center, rgba(255, 160, 20, 0.25) 0%, rgba(200, 120, 10, 0.1) 40%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />
      {/* Right-side warm accent (behind iPad) */}
      <div
        className="absolute z-10 pointer-events-none hidden sm:block"
        style={{
          top: '45%',
          right: '5%',
          width: '30%',
          height: '40%',
          background:
            'radial-gradient(ellipse at center, rgba(255, 150, 20, 0.25) 0%, rgba(200, 110, 10, 0.1) 40%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />
      {/* Floor reflection - warm golden light on bottom surface */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[40%] z-10 pointer-events-none"
        style={{
          background:
            'linear-gradient(0deg, rgba(200, 140, 30, 0.3) 0%, rgba(180, 120, 20, 0.15) 20%, rgba(140, 90, 10, 0.05) 50%, transparent 100%)',
        }}
      />
      {/* Hot spot - bright golden center at bottom of devices */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-10 pointer-events-none"
        style={{
          bottom: '20%',
          width: '40%',
          height: '20%',
          background:
            'radial-gradient(ellipse at center, rgba(255, 200, 50, 0.3) 0%, rgba(230, 160, 30, 0.15) 40%, transparent 70%)',
          filter: 'blur(30px)',
        }}
      />

      {/* Device Mockups - Laptop | iPhone (center) | iPad */}
      <div ref={devicesContainerRef} className="relative z-30 flex items-end justify-center mt-8 md:mt-12 px-4 pb-4">
        {/* Laptop - left, hidden on mobile */}
        <div
          className="relative flex-shrink-0 z-20 hidden sm:block"
          style={{ width: 'clamp(340px, 50vw, 700px)' }}
        >
          <img
            src="/images/Laptop.png"
            alt="Laptop"
            className="w-full h-auto relative z-20 pointer-events-none select-none"
            draggable={false}
          />
          <video
            ref={laptopVideoRef}
            muted
            playsInline
            preload="none"
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            className="absolute z-10 object-cover"
            style={{
              top: '22%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '53%',
              height: '60%',
              borderRadius: '1%',
              opacity: 0,
              ...videoTransition,
            }}
          />
        </div>

        {/* iPhone - center, always visible */}
        <div
          className="relative flex-shrink-0 z-40 sm:-ml-19 md:-ml-28 lg:-ml-30 mb-2"
          style={{ width: 'clamp(160px, 30vw, 210px)' }}
        >
          <img
            src="/images/iphone-medium.png"
            alt="iPhone"
            className="w-full h-auto relative z-20 pointer-events-none select-none"
            draggable={false}
          />
          <video
            ref={iphoneVideoRef}
            muted
            playsInline
            preload="none"
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            className="absolute z-10 object-cover"
            style={{
              top: '2.5%',
              left: '5%',
              width: '90%',
              height: '95%',
              borderRadius: '12%',
              opacity: 0,
              ...videoTransition,
            }}
          />
        </div>

        {/* iPad - right, hidden on mobile */}
        <div
          className="relative flex-shrink-0 z-30 -ml-4 md:-ml-6 hidden sm:block"
          style={{ width: 'clamp(220px, 34vw, 450px)' }}
        >
          <img
            src="/images/ipad-medium.png"
            alt="iPad"
            className="w-full h-auto relative z-20 pointer-events-none select-none"
            draggable={false}
          />
          <video
            ref={ipadVideoRef}
            muted
            playsInline
            preload="none"
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            className="absolute z-10 object-cover"
            style={{
              top: '4.5%',
              left: '4.5%',
              width: '92%',
              height: '91%',
              borderRadius: '2.5%',
              opacity: 0,
              ...videoTransition,
            }}
          />
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="relative z-40 flex justify-center gap-5 mt-6 md:mt-8 pb-10">
        <button
          onClick={() => navigate('/get-started')}
          className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-8 md:px-12 py-3.5 md:py-4 rounded-lg text-base md:text-lg transition-all hover:scale-105 shadow-lg shadow-yellow-600/20"
        >
          Get Started
        </button>
        <button
          onClick={() => navigate('/sign-in')}
          className="border-2 border-gray-500 hover:border-gray-300 text-white font-bold px-8 md:px-12 py-3.5 md:py-4 rounded-lg text-base md:text-lg transition-all hover:scale-105"
        >
          Sign In
        </button>
      </div>
    </div>
  );
}
